import {Injectable} from '@angular/core';
import {from as fromRxjs, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
  AsaxElasticService,
  buildQueryGreaterEqual,
  buildQueryLessEqual,
  buildQueryTermsEqual,
  ElasticFiltersService
} from 'asax-elastic';
import {Footprint, Interval} from 'asax-common';


interface Limit {
  readonly from: number;
  readonly size: number;
}

export const almaBands: any[] = [
  {from: 84, to: 116},
  {from: 120, to: 163},
  {from: 163, to: 211},
  {from: 211, to: 275},
  {from: 275, to: 372},
  {from: 385, to: 500},
  {from: 602, to: 720},
  {from: 787, to: 950}
];

const maximumWindowElasticResultSize = 10000;

@Injectable({
  providedIn: 'root'
})
export class FrequencyService {
  constructor(private readonly asaxElasticService: AsaxElasticService,
              private readonly elasticFiltersService: ElasticFiltersService) {
  }

  fetchFrequencies(filters: any, filtersMappingSearchProperties: any): Observable<any> {
    const elasticDSLQueryBodyFromFilters = this.elasticFiltersService.buildElasticDSLQueryBodyFromFilters(filters, filtersMappingSearchProperties);
    const aggregations = buildAggregationFrequencyIntervals();

    return this.asaxElasticService.searchByQuery({
      index: 'observations',
      body: {from: 0, size: 0, query: elasticDSLQueryBodyFromFilters, aggregations}
    }).pipe(
      map((response: any) => mapAggregationFrequencyIntervalsResponseToFrequencyBands(response)),
      catchError(() => of([]))
    );
  }

  fetchFrequenciesForFootprint(footprint: Footprint): Observable<any> {
    if (footprint?.datasetId) {
      return this.asaxElasticService.searchByQuery({
        index: 'observations',
        body: {
          _source: ['energy.freqMin', 'energy.freqMax'],
          query: {terms: {_id: [footprint.datasetId]}}
        }
      }).pipe(
        map((data: any) => data.hits.hits[0]._source)
      );
    }

    return of(undefined);
  }

  fetchFrequenciesIntervalsForObservation(id: string): Observable<any> {
    return this.asaxElasticService.searchByQuery({
      index: 'observations',
      body: {
        _source: ['energy.freqMin', 'energy.freqMax'],
        query: {term: {_id: id}}
      }
    }).pipe(
      map((response: any) => response?.hits?.hits[0]?._source.energy.map(energy => ({
        from: energy.freqMin,
        to: energy.freqMax
      })) || []),
      catchError(() => of([]))
    );
  }

  fetchFrequenciesIntervalsForProject(id: string): Observable<any> {
    const aggregations = buildAggregationFrequencyIntervals();
    return this.asaxElasticService.searchByQuery({
      index: 'observations',
      body: {
        query: {term: {'project.code': id}},
        aggregations
      }
    }).pipe(
      map((response: any) => mapAggregationFrequencyIntervalsResponseToFrequencyBands(response)),
      catchError(() => of([]))
    );
  }

  fetchFrequenciesIntervalsForPublication(id: string): Observable<any> {
    const aggregations = buildAggregationFrequencyIntervals();
    return this.asaxElasticService.searchByQuery({
      index: 'observations',
      body: {
        query: {
          nested: {
            path: 'publications',
            query: {term: {'publications.bibCode': id}}
          }
        },
        aggregations
      }
    }).pipe(
      map((response: any) => mapAggregationFrequencyIntervalsResponseToFrequencyBands(response)),
      catchError(() => of([]))
    );
  }

  fetchSelectedFrequenciesIntervalsForObservations(ids: string[]): Observable<any> {
    const promises: Promise<any>[] = [];
    const numberOfRows = ids?.length;

    const aggregations = buildAggregationFrequencyIntervals();

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const idsChunk = ids.slice(limit.from, limit.from + limit.size);
      const idsChunkDSLQuery = buildQueryTermsEqual('_id', idsChunk);
      const promiseElasticSearchChunk = this.asaxElasticService.searchByQuery({
        index: 'observations', body: {size: 0, query: idsChunkDSLQuery, aggregations}
      }).toPromise();
      promises.push(promiseElasticSearchChunk);
    }

    return fromRxjs(Promise.all(promises)).pipe(
      map((response: any) => mapPromisesAggregationFrequencyIntervalsResponseToFrequencyBands(response)),
      catchError(() => [])
    );
  }

  fetchSelectedFrequenciesIntervalsForProjects(ids: string[]): Observable<any> {
    const promises: Promise<any>[] = [];
    const numberOfRows = ids?.length;

    const aggregations = buildAggregationFrequencyIntervals();

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const idsChunk = ids.slice(limit.from, limit.from + limit.size);
      const idsChunkDSLQuery = buildQueryTermsEqual('project.code', idsChunk);
      const promiseElasticSearchChunk = this.asaxElasticService.searchByQuery({
        index: 'observations', body: {size: 0, query: idsChunkDSLQuery, aggregations}
      }).toPromise();
      promises.push(promiseElasticSearchChunk);
    }

    return fromRxjs(Promise.all(promises)).pipe(
      map((response: any) => mapPromisesAggregationFrequencyIntervalsResponseToFrequencyBands(response)),
      catchError(() => [])
    );
  }

  fetchSelectedFrequenciesIntervalsForPublications(ids: string[]): Observable<any> {
    const promises: Promise<any>[] = [];
    const numberOfRows = ids?.length;

    const aggregations = buildAggregationFrequencyIntervals();

    for (const limit of getLimits(numberOfRows, maximumWindowElasticResultSize)) {
      const idsChunk = ids.slice(limit.from, limit.from + limit.size);
      const idsChunkDSLQuery = buildQueryTermsEqual('publications.bibCode', idsChunk);
      const promiseElasticSearchChunk = this.asaxElasticService.searchByQuery({
        index: 'observations', body: {
          size: 0, query: {
            nested: {
              path: 'publications',
              query: idsChunkDSLQuery
            }
          }, aggregations
        }
      }).toPromise();
      promises.push(promiseElasticSearchChunk);
    }

    return fromRxjs(Promise.all(promises)).pipe(
      map((response: any) => mapPromisesAggregationFrequencyIntervalsResponseToFrequencyBands(response)),
      catchError(() => [])
    );
  }
}

const buildAggregationFrequencyIntervals = (): any => {
  const aggregations = {};
  const frequencyIntervals = buildFrequencyIntervals();
  frequencyIntervals.forEach(interval => {
    const rangeLte = buildQueryLessEqual('energy.freqMin', interval.to);
    const rangeGte = buildQueryGreaterEqual('energy.freqMax', interval.from);
    const filter = {filter: {bool: {must: [rangeLte, rangeGte]}}};

    aggregations[`${interval.from}-${interval.to}`] = {nested: {path: 'energy'}, aggs: {docs: filter}};
  });

  return aggregations;
};

const buildFrequencyIntervals = (): any[] => {
  return almaBands.map(almaBand => buildIntervalsBetweenFromAndTo(10, almaBand.from, almaBand.to))
    .flat();
};

const buildIntervalsBetweenFromAndTo = (numberNumberOfIntervals: number, from: number, to: number): any[] => {
  const intervalSize = (to - from) / numberNumberOfIntervals;
  const array = Array.from(new Array(numberNumberOfIntervals), (val, index) => index + 1);
  return array.map((e, i) => {
    const intervalFrom = from + (i * intervalSize);
    const intervalTo = intervalFrom + intervalSize;
    return {from: intervalFrom.toFixed(2), to: intervalTo.toFixed(2)};
  });
};

const mapPromisesAggregationFrequencyIntervalsResponseToFrequencyBands = (responses: any[]): any[] => {
  return mergeIntervals(
    responses.map(response => mapAggregationFrequencyIntervalsResponseToFrequencyBands(response))
      .flat()
  );
};


const mapAggregationFrequencyIntervalsResponseToFrequencyBands = (response: any): any[] => {
  const {aggregations} = response;
  const intervals = [];

  for (const interval in aggregations || {}) {
    if (aggregations.hasOwnProperty(interval)) {
      if (aggregations[interval]?.docs?.doc_count > 0) {
        const tokens = interval.split('-');
        const from = +tokens[0];
        const to = +tokens[1];
        intervals.push({from, to, count: aggregations[interval].docs.doc_count});
      }
    }
  }

  return intervals;
};

const getLimits = (total: number, size: number): Limit[] => {
  const array = Array.from(new Array(Math.floor(total / size)), (val, index) => index + 1);
  const limits = array.map((value, index) => ({from: index * size, size}));
  limits.push({from: limits.length * size, size: total % size});
  return limits;
};


const mergeIntervals = (intervals: Interval[]): Interval[] => {
  const result = [];
  let last;

  intervals.sort((a: Interval, b: Interval) => a.from - b.from || a.to - b.to);
  intervals.forEach((interval: Interval) => {
    if (!last || interval.from > last.to) {
      result.push(last = interval);
    } else if (interval.to > last.to) {
      last.to = interval.to;
    }
  });

  return result;
};
